/**
 * Audits — list / detail / create + report presign.
 *
 * - `useAudits({limit, offset})` → `GET /v1/audits`.
 * - `useAudit(id)` → `GET /v1/audits/{id}`. Polls every 3 s while
 *   `status ∈ pending|running`; stops on terminal. Driven by react-
 *   query's `refetchInterval` — returning `false` halts polling.
 * - `useCreateAudit()` → `POST /v1/audits`. Accepts `Idempotency-Key`
 *   via the mutation variable so the caller (Home "Run it" flow) can
 *   make the request safely retriable.
 * - `useAuditReport(id)` → `GET /v1/audits/{id}/report`. Returns a
 *   presigned R2 URL valid for 300 s. `enabled: false` by default so
 *   the caller opts in — we don't want every audit detail view to
 *   burn a presign.
 *
 * Invalidation:
 *   - A successful `POST /v1/audits` invalidates `auditKeys.lists()`
 *     so the "Recent audits" peek on Home reflects the new row.
 *   - Usage is invalidated because MTD on-demand incremented.
 */
import type { QueryClient } from '@tanstack/react-query';
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiFetch } from '../api';
import type {
  AuditReportResponse,
  AuditResponse,
  CreateAuditBody,
  ListAuditsResponse,
} from '../api-types';
import { auditKeys } from './query-keys';
import { invalidateUsage } from './usage';

export interface AuditsFilter {
  limit?: number;
  offset?: number;
  include_ids?: boolean;
  /** Optional server-side scope to a single site. */
  site_uuid?: string;
  /** Optional server-side scope to a single page (within its site). */
  page_uuid?: string;
  /** Lower bound on `created_at` (inclusive), ISO 8601 — scopes to a time
   *  window (e.g. the per-page trend charts' range pill). */
  created_after?: string;
  /** Drop variant treatment runs server-side (so they can't bury baseline audits). */
  exclude_variant?: boolean;
}

function buildQueryString(filter: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useAudits(
  filter: AuditsFilter = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<ListAuditsResponse> {
  return useQuery({
    queryKey: auditKeys.list(filter),
    queryFn: () => apiFetch<ListAuditsResponse>(`/v1/audits${buildQueryString({ ...filter })}`),
    enabled: options.enabled ?? true,
  });
}

/**
 * Poll interval while an audit is in flight. 3 s matches the phase
 * doc + the legacy dashboard's behaviour; Lighthouse runs take 15–60
 * s typically so 3 s gives a ~5–20× snappy feel without hammering the
 * API.
 */
const AUDIT_POLL_MS = 3000;

export function useAudit(
  id: string | null | undefined,
  options: { includeIds?: boolean; enabled?: boolean } = {},
): UseQueryResult<AuditResponse> {
  const enabled = (options.enabled ?? true) && !!id;
  const qs = options.includeIds ? '?include_ids=true' : '';
  return useQuery({
    queryKey: auditKeys.detail(id ?? ''),
    queryFn: () => apiFetch<AuditResponse>(`/v1/audits/${id}${qs}`),
    enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (!status) return false;
      return status === 'pending' || status === 'running' ? AUDIT_POLL_MS : false;
    },
    // While polling we don't want retries to stomp the interval — one
    // transient 500 is fine, we just wait 3 s and try again.
    retry: false,
  });
}

export interface SpotAuditVariables {
  body: CreateAuditBody;
  idempotencyKey?: string;
}

/**
 * Legacy alias for source-compat with call sites that pre-date the
 * audit-verb refactor. Prefer `SpotAuditVariables` in new code.
 */
export type CreateAuditVariables = SpotAuditVariables;

/**
 * `spot_audit` — POST /v1/audits, BYO config. One-off audit on a URL
 * with caller-supplied device + region. Lands on the snapshot_groups
 * row with `trigger='agent'` (when called via API key) or
 * `trigger='web'` (when called via dashboard JWT) — the server
 * infers from auth context.
 *
 * Almost every audit should go through `useAuditSite` instead — it
 * reuses the site's configured matrix
 * so the data points are comparable across runs. Reach for
 * `useSpotAudit` only when the URL isn't a configured page (debug,
 * exploration, third-party domain) or when the dashboard's
 * "Re-run this combination" CTA needs to pin a specific
 * (device, region) tuple.
 */
export const useCreateAudit = useSpotAudit;

export function useSpotAudit(): UseMutationResult<
  AuditResponse | { audits: AuditResponse[]; count: number },
  Error,
  SpotAuditVariables
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ body, idempotencyKey }) =>
      apiFetch<AuditResponse | { audits: AuditResponse[]; count: number }>('/v1/audits', {
        method: 'POST',
        body,
        ...(idempotencyKey ? { idempotencyKey } : {}),
      }),
    onSuccess: async () => {
      // New audit in the workspace: lists + usage both change.
      await Promise.all([
        qc.invalidateQueries({ queryKey: auditKeys.lists() }),
        invalidateUsage(qc),
      ]);
    },
  });
}

export function useAuditReport(
  id: string | null | undefined,
  options: { enabled?: boolean } = {},
): UseQueryResult<AuditReportResponse> {
  return useQuery({
    queryKey: auditKeys.report(id ?? ''),
    queryFn: () => apiFetch<AuditReportResponse>(`/v1/audits/${id}/report`),
    enabled: !!id && (options.enabled ?? false),
    // The presigned URL is 300 s lived on the API side; cache the
    // response for 4 minutes so the user can click "Download" twice
    // in a row without burning a second presign.
    staleTime: 4 * 60_000,
  });
}

export function invalidateAudits(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({ queryKey: auditKeys.all });
}
